import { useEffect, useState } from "react";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { createColumnHelper } from "@tanstack/react-table";
import { IReport, summariseDateFields } from "../../models/Reports/Report";
import { PencilIcon, PlusIcon, TrashIcon, LinkIcon } from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import { Table } from "../../components/base/Table/Table";
import { ReportQueryKeys } from "../../data/Report/keys";
import { Button } from "../../components/base/Button";
import useReportMutations from "../../data/Report/useReportMutations";
import { ReportLinksModal } from "../../components/Reports/ReportLinksModal";

const colHelper = createColumnHelper<IReport>();

export default function Reports() {
  const { remove } = useReportMutations();
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [linksModalOpen, setLinksModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<{ id: string; title: string } | null>(null);

  useEffect(() => {
    setInfo({
      name: "Reports",
      desc: "View and create reports.",
      pageTitle: "Reports",
    });
  }, []);


  const cols = [
    colHelper.accessor("id", { header: "Report ID" }),
    colHelper.accessor("title", { header: "Title" }),
    colHelper.accessor("allowDates", { header: "Allows dates?" }),
    // Shows at a glance which reports have been converted to selectable date fields and which
    // are still on the legacy both-dates-required shape.
    colHelper.accessor("dateFields", {
      header: "Date fields",
      cell: (info) => summariseDateFields(info.getValue()),
    }),
    colHelper.display({
      header: "Actions",
      cell: (info) => (
        <div className="flex items-center space-x-4">
          <PencilIcon
            className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
            onClick={() => navigate(`/reports/${info.row.original.id}/edit`)}
          />
          <LinkIcon
            className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
            onClick={() => {
              setSelectedReport({ id: info.row.original.id, title: info.row.original.title });
              setLinksModalOpen(true);
            }}
          />
          <TrashIcon
            className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
            onClick={() => { setDeleteId(info.row.original.id); setDeleteOpen(true); }}
          />
        </div>
      ),
    }),
  ];

  const handleDelete = () => {
    remove.mutate(deleteId, {
      onSuccess: () => {
        setDeleteOpen(false);
        setDeleteId("");
      },
      onError: (err) => {
        console.error(err);
      },
    });
  }

  return (
    <>
      <ConfirmModal open={deleteOpen} setOpen={setDeleteOpen}
        title={"Delete Report?"} message={`Are you sure you want to delete this report?\n It's a soft delete anyway, so don't worry too much.`}
        confirmButtonText={"Delete"}
        onConfirm={handleDelete}
        isLoading={false}
      />
      {selectedReport && (
        <ReportLinksModal
          open={linksModalOpen}
          setOpen={setLinksModalOpen}
          reportId={selectedReport.id}
          reportTitle={selectedReport.title}
        />
      )}
      <div className="space-y-6">
        <div className="flex justify-between">
          <Link to="new">
            <Button className="flex items-center space-x-2">
              <PlusIcon className="h-5 w-5 -ml-1" />
              <p>Create Report</p>
            </Button>
          </Link>
        </div>
        <Table
          url="/report"
          queryKey={ReportQueryKeys.all()}
          columns={cols}
        />
      </div>
    </>
  )
}